/* ═══════════════════════════════════════════════════════════════════════════
   👍 services/broadcastReactions.js — how many people reacted to an announcement.

   Twin of app/services/broadcastReactions.ts. Same document, same rule, same
   refcounting; only the Firestore import differs.

   🟥 A BROADCAST IS NOT ONE MESSAGE. IT IS N COPIES OF ONE MESSAGE. The fan-out
      writes a separate document into every recipient's own chat, so a reaction
      lands on the reactor's PRIVATE copy and is invisible to everybody else by
      construction. The running total therefore lives outside the copies, in one
      small public document per broadcast that only the server writes — see
      server/functions/officialReactions.js.

   🟥 THE BROWSER READS AND DOES NOT WRITE, AND THAT IS NOT AN OVERSIGHT. This
      client has never had message reactions — there is no picker, no long-press
      peek, nothing that could produce one. What it was missing is the ability to
      SEE the ones that exist, which is what left an announcement forty people
      had reacted to looking untouched here.
   ═══════════════════════════════════════════════════════════════════════════ */
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * `{'❤️': 12, '👍': 3}` → the emoji, busiest first. PURE.
 *
 * 🟢 EMPTY AND NEGATIVE BUCKETS ARE DROPPED. `increment` has no floor, so a
 *    total repaired by hand — or written before this file existed — can hold a
 *    key at zero or below; refusing to draw it needs no migration.
 */
export function orderCounts(counts) {
  const src = counts && typeof counts === 'object' ? counts : {};
  return Object.entries(src)
    .map(([e, n]) => [e, Number(n) || 0])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/**
 * What the pill shows for one message. PURE.
 *
 * 🟥 THE AGGREGATE WINS WHOLE, never merged with the message's own map. The
 *    server counted the reactor when it wrote their copy, so adding the local
 *    map on top counts them twice — and only for themselves, which is the most
 *    confusing possible version of a wrong number.
 * 🟢 …AND ONLY WHEN IT HAS SOMETHING TO SAY. A broadcast nobody has reacted to
 *    has no aggregate document at all, and the message's own map is then the
 *    honest answer.
 */
export function reactionDisplay(reactions, agg) {
  const ordered = orderCounts(agg && agg.counts);
  if (ordered.length) {
    return {
      emojis: ordered.slice(0, 3).map(([e]) => e),
      total: ordered.reduce((n, [, c]) => n + c, 0),
    };
  }
  const local = Object.entries(reactions && typeof reactions === 'object' ? reactions : {})
    .map(([e, u]) => [e, Array.isArray(u) ? u.length : (u ? 1 : 0)])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return {
    emojis: local.slice(0, 3).map(([e]) => e),
    total: local.reduce((n, [, c]) => n + c, 0),
  };
}

/* ── one listener per broadcast, however many bubbles want it ─────────────── */

const _live = new Map();

/** The last value seen for a broadcast, so a remounting row draws immediately. */
export function peekBroadcastCounts(id) {
  if (!id) return null;
  const e = _live.get(String(id));
  return (e && e.value) || null;
}

/**
 * Watch one broadcast's public totals. Returns the unsubscribe.
 *
 * 🟢 REFCOUNTED. A broadcast carrying four photos is four messages with the same
 *    id, and a chat can hold dozens of announcements; a listener per row would
 *    be dozens of duplicate snapshot streams for the same document.
 */
export function subscribeBroadcastCounts(id, cb) {
  const key = String(id || '');
  if (!key) return () => {};

  let e = _live.get(key);
  if (!e) {
    e = { refs: 0, stop: null, value: null, subs: new Set() };
    _live.set(key, e);
    try {
      e.stop = onSnapshot(doc(db, 'broadcastReactions', key), (snap) => {
        const d = snap && snap.exists() ? snap.data() : null;
        const v = d ? { counts: d.counts || {}, total: Math.max(0, Number(d.total) || 0) } : null;
        const cur = _live.get(key);
        if (!cur) return;
        cur.value = v;
        cur.subs.forEach((fn) => { try { fn(v); } catch { /* one bad row must not stop the rest */ } });
      }, () => {
        /* 🟥 A DENIED READ IS A NORMAL OUTCOME, not an error worth logging on
           every announcement: the totals need a rules block a project may not
           have pasted yet. The pill falls back to the message's own map, which
           is exactly what this drew before the collection existed. */
      });
    } catch { e.stop = null; }
  }

  e.refs += 1;
  e.subs.add(cb);
  if (e.value) { try { cb(e.value); } catch { /* the caller unmounted */ } }

  let done = false;
  return () => {
    if (done) return;        // 🟥 a double unsubscribe must not free somebody else's listener
    done = true;
    const cur = _live.get(key);
    if (!cur) return;
    cur.subs.delete(cb);
    cur.refs -= 1;
    if (cur.refs <= 0) {
      try { if (cur.stop) cur.stop(); } catch { /* already gone */ }
      _live.delete(key);
    }
  };
}
